import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog'; // Import DialogService
import { Question, RfpCreateDto } from 'src/app/domain/rfp.model';
import { RfpService } from 'src/app/services/rfp/rfp.service';
import { SpinnerServiceService } from 'src/app/shared/services/spinner-service.service';
import { LoggingUser, UserConfigService } from 'src/app/shared/services/user-config.service';

@Component({
  selector: 'app-rfp-form',
  templateUrl: './rfp-form.component.html',
  styleUrls: ['./rfp-form.component.scss'],
  providers: [DialogService] // Add DialogService to providers
})
export class RfpFormComponent implements OnInit {
  @ViewChild('questionModal') questionModal: ElementRef;
  rfpForm: FormGroup;
  currentMode: 'add' | 'edit' | 'view' = 'add';
  modes = [
    { label: 'Add', value: 'add' },
    { label: 'Edit', value: 'edit' },
    { label: 'View', value: 'view' }
  ];

  steps = [
    { label: 'RFP Details', command: () => this.index = 0 },
    { label: 'Q&A', command: () => this.index = 1 },
    { label: 'Comments', command: () => this.index = 2 },
    { label: 'History', command: () => this.index = 3 }
  ];
 // index: number = 0;
  deploymentTypes = ['On-Premise', 'Cloud', 'Hyperid'];
  productGroups = [
    'Websphere Hybrid Edition',
    'Data and AI',
    'Business Automation',
    'Integration',
    'Sustainability',
    'FinOps/AIOps',
    'Generative AI'
  ];

  newQuestion: string = '';
  newAnswer: string = '';
  customers: any[] = [];
  techLeads: any[] = [];
  currentUser: LoggingUser;

  qaList: any[] = []; // Q&A list
  comments: any[] = []; // Comments list
  auditHistory: any[] = []; // Audit history list
  newComment: string = ''; // New comment input
  newCommentTitle: string = ''; // Title for new comment
  newCommentDescription: string = ''; // Description for new comment
  uploadedFiles: any[] = []; // To store uploaded files

  // New properties to resolve errors
  loading: boolean = false; // For spinner
  msgs: any[] = []; // For p-messages
  index: number = 0; // For p-tabView activeIndex
  isCollapsed: { [key: string]: boolean } = { rfpDetails: false }; // For collapsible sections

  displayDialog: boolean = false; // Add this line to control dialog visibility
  rfpId: any;
  selectedQuestions: Question[] = [];


  displayEditDialog: boolean = false; // Add this line to control edit dialog visibility
  editQuestion: string = ''; // Add this line for editing question
  editAnswer: string = ''; // Add this line for editing answer
  selectedQa: any = null; // Add this line to store the selected Q&A for editing
  displayQuestionIdDialog: boolean = false;
  selectedQuestionId: number;
  questions: Question[] = [];
  questionCategories: string[] = []; // To store unique categories
  filteredQuestions: Question[] = []; // To store questions filtered by category
  selectedCategory: string = null; // To store selected category
  newCategory: string = '';
  editCategory: string = '';
  constructor(
    private fb: FormBuilder,
    private messageService: MessageService,
    private dialogService: DialogService,
    private rfpService: RfpService,
    private user: UserConfigService,
    private router: Router,
  ) {
    this.createForm();

    const navigation = this.router.getCurrentNavigation();
    this.currentMode = navigation?.extras.state?.mode || 'add';
    this.rfpId = navigation?.extras.state?.rfpId;
    if (this.rfpId) {
      this.loadRfpById(this.rfpId);
    }
  }

  ngOnInit() {
    this.loadCustomers();
    this.loadTechLeads();
    this.loadCurrentUser();
    this.loadQuestions();
    if (this.rfpId) {
      this.loadRfpById(this.rfpId);
    }

  }

  loadQuestions() {
    this.rfpService.getQuestions().subscribe(
      (questions) => {
        this.questions = questions;
        
        const categories = new Set<string>();
        this.questions.forEach(q => {
          if (q.category) {
            categories.add(q.category);
          }
        });
        this.questionCategories = Array.from(categories);
      },
      (error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load questions'
        });
      }
    );
  }
  
  onCategoryChange(event: any) {
    const selectedCategory = event.value;
    this.filteredQuestions = this.questions.filter(q => q.category === selectedCategory);
    this.selectedQuestions = []; // Reset selected questions when category changes
  }

  showAddQuestionIdDialog() {
    this.displayQuestionIdDialog = true;
  }

  nextTab() {
    if (this.index < this.steps.length - 1) {
      this.index++;
    }
  }

  // Method to navigate to the previous tab
  previousTab() {
    if (this.index > 0) {
      this.index--;
    }
  }

  addQuestionIdAndAnswer() {
    if (this.selectedQuestionId && this.newAnswer?.trim()) {
      const selectedQuestion = this.questions.find(q => q.id === this.selectedQuestionId);
      this.qaList.unshift({
        id: this.qaList.length + 1,
        question: selectedQuestion.question,
        answer: this.newAnswer,
        category: selectedQuestion.category,
      });

      // Clear the form
      this.selectedQuestionId = null;
      this.newAnswer = '';

      // Hide the dialog
      this.displayQuestionIdDialog = false;

      // Show success message
      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Question added successfully'
      });
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Question ID and answer are required'
      });
    }
  }
  createForm() {
    this.rfpForm = this.fb.group({
      customerName: ['', Validators.required],
      deploymentType: ['', Validators.required],
      productGroup: ['', Validators.required],
      rfpReferenceNumber: ['', Validators.required],
      rfpLink: [''],
      rfpFiles: [null],
      rfpScope: [''],
      rfpScopeDetails: [''],
      tags: [[]],
      techConsultant: [''],
      inquiryDate: ['', Validators.required],
      submissionDate: ['', Validators.required],
      projectDuration: ['', Validators.required],
      durationUnit: ['', Validators.required],
      location: ['', Validators.required],
      initialBidBond: ['', Validators.required],
      finalBidBond: ['', Validators.required],
      resources: [false],
      license: [false],
      training: [false],
      trainingDescription: [''],
      support: [false],
      supportDuration: [''],
      supportDurationUnit: [''],
      supportDescription: [''],
      summary: ['']
    });
  }

  loadRfpById(id: number) {
    this.rfpService.getRfpById(id).subscribe(
      (rfp) => {
        const [projectDurationValue, projectDurationUnit] = rfp.projectDuration ? this.splitDuration(rfp.projectDuration) : ['', ''];
        const [supportDurationValue, supportDurationUnit] = rfp.supportDuration ? this.splitDuration(rfp.supportDuration) : ['', ''];
        this.rfpForm.patchValue({
          customerName: rfp.customerId,
          deploymentType: rfp.deploymentType,
          productGroup: rfp.productGroup,
          rfpReferenceNumber: rfp.rfpReferenceNumber,
          rfpLink: rfp.rfpLink,
          rfpScope: rfp.rfpScope,
          rfpScopeDetails: rfp.rfpScopeDetails,
          tags: rfp.rfpTagNames,
          techConsultant: rfp.techLeadId,
          inquiryDate: new Date(rfp.inquiryDate),
          submissionDate: new Date(rfp.submissionDate),
          projectDuration: projectDurationValue,
          durationUnit: projectDurationUnit,
          location: rfp.location,
          initialBidBond: rfp.initialBidBond,
          finalBidBond: rfp.finalBidBond,
          resources: rfp.resourcesAvailable,
          license: rfp.licenseAvailable,
          training: rfp.trainingAvailable,
          trainingDescription: rfp.trainingDetails,
          support: rfp.supportAvailable,
          supportDuration: supportDurationValue,
          supportDurationUnit: supportDurationUnit,
          supportDescription: rfp.supportDescription,
          summary: rfp.summary
        });
        this.auditHistory = rfp.auditHistory;
        this.comments = rfp.comments.map(comment => ({
          title: comment.title,
          content: comment.content,
          commentLocalDateTime: new Date(comment.commentLocalDateTime).toISOString(), // Ensure ISO format
          commentBy: comment.commentBy
        }));
        this.qaList = rfp.questions;
        this.uploadedFiles = rfp.attachments || [];


      }
    );
  }

  splitDuration(duration: string): [string, string] {
    const parts = duration.split(' ');
    return [parts[0], parts[1]];
  }
  loadCustomers() {
    this.rfpService.getCustomers().subscribe(customers => {
      this.customers = customers.map(customer => ({
        label: customer.customerName,
        value: customer.id
      }));
    });
  }

  loadTechLeads() {
    this.rfpService.getTechLeads().subscribe(techLeads => {
      this.techLeads = techLeads.map(techLead => ({
        label: techLead.fullName,
        value: techLead.id
      }));
    }
    );
  }



  isViewMode(): boolean {
    return this.currentMode === 'view';
  }

  onModeChange(event: any) {
    if (event.value === 'view') {
      this.rfpForm.disable();
    } else {
      this.rfpForm.enable();
    }
  }


  onSubmit(event: Event) {
    event.preventDefault();
    if (this.rfpForm.valid) {
      SpinnerServiceService.showSpinner();

      const projectDuration = `${this.rfpForm.value.projectDuration} ${this.rfpForm.value.durationUnit}`;
      const supportDuration = `${this.rfpForm.value.supportDuration} ${this.rfpForm.value.supportDurationUnit}`;

      const rfpData: RfpCreateDto = {
        customerId: this.rfpForm.value.customerName,
        techLeadId: this.rfpForm.value.techConsultant,
        productGroup: this.rfpForm.value.productGroup,
        rfpLink: this.rfpForm.value.rfpLink,
        rfpScope: this.rfpForm.value.rfpScope,
        rfpScopeDetails: this.rfpForm.value.rfpScopeDetails,
        rfpReferenceNumber: this.rfpForm.value.rfpReferenceNumber,
        deploymentType: this.rfpForm.value.deploymentType,
        rfpTagNames: this.rfpForm.value.tags ? this.rfpForm.value.tags.map(tag => tag.trim()) : [],
        inquiryDate: this.rfpForm.value.inquiryDate,
        projectDuration: projectDuration,
        location: this.rfpForm.value.location,
        submissionDate: this.rfpForm.value.submissionDate,
        initialBidBond: this.rfpForm.value.initialBidBond,
        finalBidBond: this.rfpForm.value.finalBidBond,
        resourcesAvailable: this.rfpForm.value.resources,
        licenseAvailable: this.rfpForm.value.license,
        trainingAvailable: this.rfpForm.value.training,
        trainingDetails: this.rfpForm.value.trainingDescription,
        supportAvailable: this.rfpForm.value.support,
        supportDescription: this.rfpForm.value.supportDescription,
        supportDuration: supportDuration,
        comments: this.comments.map(comment => ({
          title: comment.title,
          content: comment.content,
          commentLocalDateTime: comment.commentLocalDateTime,
          commentBy: comment.commentBy
        })),
        summary: this.rfpForm.value.summary,
        questions: this.qaList
          .filter(qa => qa.question != null && qa.question.trim() !== '')
          .map(qa => ({
            question: qa.question,
            answer: qa.answer || null,
            questionId: qa.questionId || null,
            category: qa.category || null
          })),
        currentUser: this.currentUser.firstName + ' ' + this.currentUser.lastName,
        attachments: this.uploadedFiles
      };

      if (this.currentMode === 'add') {
        this.rfpService.addRfp(rfpData).subscribe(
          (response) => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'RFP added successfully'
            });
            this.rfpForm.reset();
            this.comments = [];
            this.qaList = [];
            SpinnerServiceService.hideSpinner();
            this.router.navigate(['/rfp']);
          },
          (error) => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to add RFP'
            });
            SpinnerServiceService.hideSpinner();
          }
        );
      } else if (this.currentMode === 'edit') {
        this.rfpService.updateRfp(this.rfpId, rfpData).subscribe(
          (response) => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'RFP updated successfully'
            });
            this.router.navigate(['/rfp']);
          },
          (error) => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to update RFP'
            });
          }
        );
      }
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Please fill in all required fields'
      });
    }
  }

  onUpload(event: any) {
    for (let file of event.files) {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        const base64String = e.target.result.split(',')[1];
        this.uploadedFiles.push({
          attachmentName: file.name,
          content: base64String,
          contentType: file.type
        });
      };
      reader.readAsDataURL(file);
    }
    this.messageService.add({
      severity: 'info',
      summary: 'Success',
      detail: 'File Uploaded'
    });
  }

  downloadAttachment(attachment: { attachmentName: string; content: string; contentType: string }) {
    const byteCharacters = atob(attachment.content);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: attachment.contentType });

    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = attachment.attachmentName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  onTabChange(event: any) {
    this.index = event.index;
  }

  toggleCollapse(section: string) {
    this.isCollapsed[section] = !this.isCollapsed[section];
  }

  saveEditedQnA() {
    // Only validate that the answer isn't empty
    if (this.editAnswer?.trim()) {
      const index = this.qaList.findIndex(qa => qa === this.selectedQa);
      if (index !== -1) {
        // Only update the answer
        this.qaList[index].answer = this.editAnswer;
      }
  
      // Clear the form
      this.editAnswer = '';
      this.selectedQa = null;
  
      // Hide the dialog
      this.displayEditDialog = false;
  
      // Show success message
      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Answer updated successfully'
      });
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Answer is required'
      });
    }
  }
  showEditQuestionDialog(qa: any) {
    this.selectedQa = qa;
    this.editQuestion = qa.question;
    this.editAnswer = qa.answer || '';
    this.editCategory = qa.category;
    this.displayEditDialog = true;
  }

  showAddQuestionDialog() {
    this.displayDialog = true; // Show the dialog
  }

  addQnA() {
    if (this.newQuestion?.trim() && this.newCategory?.trim()) {
      this.qaList.unshift({
        id: this.qaList.length + 1,
        question: this.newQuestion,
        answer: this.newAnswer || null,
        category: this.newCategory // Add category
      });
  
      // Clear the form
      this.newQuestion = '';
      this.newAnswer = '';
      this.newCategory = '';
  
      // Hide the dialog
      this.displayDialog = false;
  
      // Show success message
      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Question added successfully'
      });
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Question text and category are required'
      });
    }
  }
  addComment() {
    if (this.newCommentTitle?.trim() && this.newCommentDescription?.trim()) {
      const newComment = {
        title: this.newCommentTitle,
        content: this.newCommentDescription,
        commentLocalDateTime: new Date().toISOString(), // Ensure ISO format
        commentBy: this.currentUser.firstName + ' ' + this.currentUser.lastName
      };

      this.comments.unshift(newComment);

      this.newCommentTitle = '';
      this.newCommentDescription = '';

      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Comment added successfully'
      });
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Please fill in all fields'
      });
    }
  }

  openPrevious(tabIndex: number) {
    this.index = tabIndex;
  }

  toggleTrainingDetails() {
    const trainingControl = this.rfpForm.get('trainingDescription');
    if (this.rfpForm.get('training').value) {
      trainingControl.enable();
    } else {
      trainingControl.disable();
      trainingControl.reset();
    }
  }

  toggleSupportDetails() {
    const supportDurationControl = this.rfpForm.get('supportDuration');
    const supportDurationUnitControl = this.rfpForm.get('supportDurationUnit');
    const supportDescriptionControl = this.rfpForm.get('supportDescription');

    if (this.rfpForm.get('support').value) {
      supportDurationControl.enable();
      supportDurationUnitControl.enable();
      supportDescriptionControl.enable();
    } else {
      supportDurationControl.disable();
      supportDurationUnitControl.disable();
      supportDescriptionControl.disable();
      supportDurationControl.reset();
      supportDurationUnitControl.reset();
      supportDescriptionControl.reset();
    }
  }
  onTagAdd(event: any) {
    console.log('Tag added:', event.value);
    console.log('Current tags array:', this.rfpForm.get('tags').value);
  }

  onTagRemove(event: any) {
    console.log('Tag removed:', event.value);
    console.log('Current tags array:', this.rfpForm.get('tags').value);
  }
  loadCurrentUser() {
    this.user.getLoggingUser().subscribe(
      (user) => {
        this.currentUser = user;
        console.log('Current User:', this.currentUser);
      }
    );
  }

  addQuestionsWithoutAnswers() {
    if (this.selectedQuestions && this.selectedQuestions.length > 0) {
      // For each selected question
      this.selectedQuestions.forEach(selectedQuestion => {
        this.qaList.unshift({
          id: this.qaList.length + 1,
          question: selectedQuestion.question,
          answer: null, // Empty answer
          category: selectedQuestion.category,
          questionId: selectedQuestion.id // Store the original question ID
        });
      });
  
      // Show success message
      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: `${this.selectedQuestions.length} Question(s) added successfully`
      });
  
      // Clear the form
      this.selectedQuestions = [];
  
      // Hide the dialog
      this.displayQuestionIdDialog = false;
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Please select at least one question'
      });
    }
  }
}
